// API Response Type
export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  success: boolean;
}

import { supabase } from './supabase';

/**
 * Creates a post inside a specific group.
 *
 * @param groupId - The UUID of the group where the post will be stored.
 * @param data    - Arbitrary post fields (e.g., title, content).
 * @returns       - Standardised ApiResponse object.
 */
export async function createGroupPost(
  groupId: string,
  data: Record<string, any>
): Promise<ApiResponse<any>> {
  try {
    // Ensure we have a logged-in user
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError) {
      console.error('Supabase auth error:', authError);
      return {
        data: null,
        error: authError.message,
        success: false,
      };
    }

    if (!user) {
      return {
        data: null,
        error: 'Authentication required',
        success: false,
      };
    }

    // Build the post payload
    const postData = {
      ...data,
      group_id: groupId,
      author_id: user.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Insert the post and immediately select the inserted row
    const {
      data: newPost,
      error: insertError,
    } = await supabase
      .from('posts')
      .insert(postData)
      .select()
      .single();

    // Defensive check – Supabase can return undefined data without error in rare cases
    if (insertError || !newPost) {
      const msg = insertError?.message ?? 'Failed to retrieve created post';
      console.error('Error creating group post:', msg);
      return {
        data: null,
        error: msg,
        success: false,
      };
    }

    return {
      data: newPost,
      error: null,
      success: true,
    };
  } catch (err) {
    console.error('Unexpected error creating group post:', err);
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Failed to create post',
      success: false,
    };
  }
}

// Export as part of groupsApi for consistency
export const groupsApi = {
  createGroupPost,
};

// Export as part of postsApi for forum posts
export const postsApi = {
  createPost: async (data: Record<string, any>): Promise<ApiResponse<any>> => {
    try {
      // Ensure we have a logged-in user
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) {
        console.error('Supabase auth error:', authError);
        return {
          data: null,
          error: authError.message,
          success: false,
        };
      }

      if (!user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      // Build the post payload
      const postData = {
        ...data,
        author_id: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Insert the post and immediately select the inserted row
      const {
        data: newPost,
        error: insertError,
      } = await supabase
        .from('posts')
        .insert(postData)
        .select()
        .single();

      if (insertError || !newPost) {
        const msg = insertError?.message ?? 'Failed to retrieve created post';
        console.error('Error creating post:', msg);
        return {
          data: null,
          error: msg,
          success: false,
        };
      }

      return {
        data: newPost,
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error creating post:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to create post',
        success: false,
      };
    }
  },
};

// Export reputation API
export const reputationApi = {
  getUserStats: async (userId: string): Promise<ApiResponse<any>> => {
    try {
      // Fetch user profile with reputation
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('reputation, created_at')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        return {
          data: null,
          error: profileError.message,
          success: false,
        };
      }

      // Count posts
      const { count: postsCount } = await supabase
        .from('posts')
        .select('*', { count: 'exact', head: true })
        .eq('author_id', userId);

      // Count comments
      const { count: commentsCount } = await supabase
        .from('comments')
        .select('*', { count: 'exact', head: true })
        .eq('author_id', userId);

      // Count followers
      const { count: followersCount } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('following_id', userId);

      // Count following
      const { count: followingCount } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('follower_id', userId);

      // Count upvotes given
      const { count: upvotesCount } = await supabase
        .from('post_votes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('vote_type', 'upvote');

      // Count groups created
      const { count: groupsCount } = await supabase
        .from('groups')
        .select('*', { count: 'exact', head: true })
        .eq('creator_id', userId);

      // Count reviews (posts with category 'reviews')
      const { count: reviewsCount } = await supabase
        .from('posts')
        .select('*', { count: 'exact', head: true })
        .eq('author_id', userId)
        .eq('category', 'reviews');

      return {
        data: {
          posts: postsCount || 0,
          comments: commentsCount || 0,
          followers: followersCount || 0,
          following: followingCount || 0,
          reputation: profile?.reputation || 0,
          upvotesGiven: upvotesCount || 0,
          groupsCreated: groupsCount || 0,
          reviewsPosted: reviewsCount || 0,
          joinedAt: profile?.created_at || null,
        },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error fetching user stats:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to fetch user stats',
        success: false,
      };
    }
  },

  awardPoints: async (userId: string, points: number, reason: string): Promise<ApiResponse<any>> => {
    try {
      // Call the Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('award-reputation', {
        body: { userId, points, reason },
      });

      if (error) {
        console.error('Error awarding reputation points:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data,
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error awarding reputation:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to award reputation',
        success: false,
      };
    }
  },
};

// Export activities API
export const activitiesApi = {
  createActivity: async (activityData: Record<string, any>): Promise<ApiResponse<any>> => {
    try {
      // Call the Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('create-activity', {
        body: activityData,
      });

      if (error) {
        console.error('Error creating activity:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data,
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error creating activity:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to create activity',
        success: false,
      };
    }
  },
};

// Export messages API
export const messagesApi = {
  getConversations: async (): Promise<ApiResponse<any[]>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const { data, error } = await supabase
        .from('conversations')
        .select(`
          *,
          participants:conversation_participants(
            *,
            user_profile:user_profiles(*)
          ),
          last_message:messages(*)
        `)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching conversations:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: data || [],
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error fetching conversations:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to fetch conversations',
        success: false,
      };
    }
  },

  getMessages: async (conversationId: string): Promise<ApiResponse<any[]>> => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:user_profiles(username, avatar_url)
        `)
        .eq('conversation_id', conversationId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching messages:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: data || [],
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error fetching messages:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to fetch messages',
        success: false,
      };
    }
  },

  sendMessage: async (conversationId: string, content: string): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const messageData = {
        conversation_id: conversationId,
        sender_id: user.id,
        content: content.trim(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_deleted: false,
      };

      const { data, error } = await supabase
        .from('messages')
        .insert(messageData)
        .select(`
          *,
          sender:user_profiles(username, avatar_url)
        `)
        .single();

      if (error || !data) {
        console.error('Error sending message:', error);
        return {
          data: null,
          error: error?.message || 'Failed to send message',
          success: false,
        };
      }

      // Update conversation's updated_at timestamp
      await supabase
        .from('conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversationId);

      return {
        data,
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error sending message:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to send message',
        success: false,
      };
    }
  },

  deleteMessage: async (messageId: string): Promise<ApiResponse<any>> => {
    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_deleted: true })
        .eq('id', messageId);

      if (error) {
        console.error('Error deleting message:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: { id: messageId },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error deleting message:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to delete message',
        success: false,
      };
    }
  },

  createConversation: async (participantIds: string[]): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const conversationData = {
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_group: false,
      };

      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .insert(conversationData)
        .select()
        .single();

      if (convError || !conversation) {
        console.error('Error creating conversation:', convError);
        return {
          data: null,
          error: convError?.message || 'Failed to create conversation',
          success: false,
        };
      }

      // Add participants
      const participants = [user.id, ...participantIds].map(userId => ({
        conversation_id: conversation.id,
        user_id: userId,
        joined_at: new Date().toISOString(),
        last_read_at: new Date().toISOString(),
      }));

      const { error: participantsError } = await supabase
        .from('conversation_participants')
        .insert(participants);

      if (participantsError) {
        console.error('Error adding participants:', participantsError);
        return {
          data: null,
          error: participantsError.message,
          success: false,
        };
      }

      return {
        data: conversation,
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error creating conversation:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to create conversation',
        success: false,
      };
    }
  },

  createGroupConversation: async (params: { name: string; memberIds: string[]; icon?: string }): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const conversationData = {
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_group: true,
        group_name: params.name,
        group_icon: params.icon || 'ri-group-line',
      };

      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .insert(conversationData)
        .select()
        .single();

      if (convError || !conversation) {
        console.error('Error creating group conversation:', convError);
        return {
          data: null,
          error: convError?.message || 'Failed to create group',
          success: false,
        };
      }

      // Add participants (creator + members)
      const participants = [
        {
          conversation_id: conversation.id,
          user_id: user.id,
          joined_at: new Date().toISOString(),
          last_read_at: new Date().toISOString(),
          is_admin: true,
        },
        ...params.memberIds.map(userId => ({
          conversation_id: conversation.id,
          user_id: userId,
          joined_at: new Date().toISOString(),
          last_read_at: new Date().toISOString(),
          is_admin: false,
        })),
      ];

      const { error: participantsError } = await supabase
        .from('conversation_participants')
        .insert(participants);

      if (participantsError) {
        console.error('Error adding participants:', participantsError);
        return {
          data: null,
          error: participantsError.message,
          success: false,
        };
      }

      return {
        data: conversation,
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error creating group:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to create group',
        success: false,
      };
    }
  },

  inviteMembers: async (conversationId: string, memberIds: string[]): Promise<ApiResponse<any>> => {
    try {
      const participants = memberIds.map(userId => ({
        conversation_id: conversationId,
        user_id: userId,
        joined_at: new Date().toISOString(),
        last_read_at: new Date().toISOString(),
        is_admin: false,
      }));

      const { error } = await supabase
        .from('conversation_participants')
        .insert(participants);

      if (error) {
        console.error('Error inviting members:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: { conversationId, memberIds },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error inviting members:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to invite members',
        success: false,
      };
    }
  },

  leaveConversation: async (conversationId: string): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const { error } = await supabase
        .from('conversation_participants')
        .delete()
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error leaving conversation:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: { conversationId },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error leaving conversation:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to leave conversation',
        success: false,
      };
    }
  },

  removeMember: async (conversationId: string, memberUserId: string): Promise<ApiResponse<any>> => {
    try {
      const { error } = await supabase
        .from('conversation_participants')
        .delete()
        .eq('conversation_id', conversationId)
        .eq('user_id', memberUserId);

      if (error) {
        console.error('Error removing member:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: { conversationId, memberUserId },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error removing member:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to remove member',
        success: false,
      };
    }
  },

  updateGroupSettings: async (conversationId: string, settings: { name: string; icon: string }): Promise<ApiResponse<any>> => {
    try {
      const { error } = await supabase
        .from('conversations')
        .update({
          group_name: settings.name,
          group_icon: settings.icon,
          updated_at: new Date().toISOString(),
        })
        .eq('id', conversationId);

      if (error) {
        console.error('Error updating group settings:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: { conversationId, settings },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error updating group settings:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to update settings',
        success: false,
      };
    }
  },

  markAsRead: async (conversationId: string): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const { error } = await supabase
        .from('conversation_participants')
        .update({ last_read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error marking as read:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: { conversationId },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error marking as read:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to mark as read',
        success: false,
      };
    }
  },
};

// Export reports API
export const reportsApi = {
  createReport: async (reportData: {
    contentType: string;
    contentId: string;
    reportedUserId: string;
    reason: string;
    description?: string;
  }): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const reportPayload = {
        content_type: reportData.contentType,
        content_id: reportData.contentId,
        reported_user_id: reportData.reportedUserId,
        reporter_id: user.id,
        reason: reportData.reason,
        description: reportData.description || null,
        status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('reports')
        .insert(reportPayload)
        .select()
        .single();

      if (error || !data) {
        console.error('Error creating report:', error);
        return {
          data: null,
          error: error?.message || 'Failed to submit report',
          success: false,
        };
      }

      return {
        data,
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error creating report:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to submit report',
        success: false,
      };
    }
  },
};

// Export watched episodes API
export const watchedEpisodesApi = {
  getWatchedEpisodes: async (dramaId: number): Promise<ApiResponse<any[]>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const { data, error } = await supabase
        .from('watched_episodes')
        .select('*')
        .eq('user_id', user.id)
        .eq('drama_id', dramaId)
        .order('episode_number', { ascending: true });

      if (error) {
        console.error('Error fetching watched episodes:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: data || [],
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error fetching watched episodes:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to fetch watched episodes',
        success: false,
      };
    }
  },

  markAsWatched: async (dramaId: number, episodeNumber: number, completed: boolean = true): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const watchedData = {
        user_id: user.id,
        drama_id: dramaId,
        episode_number: episodeNumber,
        watched_at: new Date().toISOString(),
        completed,
        progress_seconds: 0,
      };

      const { data, error } = await supabase
        .from('watched_episodes')
        .upsert(watchedData, {
          onConflict: 'user_id,drama_id,episode_number',
        })
        .select()
        .single();

      if (error || !data) {
        console.error('Error marking episode as watched:', error);
        return {
          data: null,
          error: error?.message || 'Failed to mark episode as watched',
          success: false,
        };
      }

      return {
        data,
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error marking episode as watched:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to mark episode as watched',
        success: false,
      };
    }
  },

  markAsUnwatched: async (dramaId: number, episodeNumber: number): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const { error } = await supabase
        .from('watched_episodes')
        .delete()
        .eq('user_id', user.id)
        .eq('drama_id', dramaId)
        .eq('episode_number', episodeNumber);

      if (error) {
        console.error('Error removing watched episode:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: { dramaId, episodeNumber },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error removing watched episode:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to remove watched episode',
        success: false,
      };
    }
  },

  markAllAsWatched: async (dramaId: number, totalEpisodes: number): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const watchedData = Array.from({ length: totalEpisodes }, (_, i) => ({
        user_id: user.id,
        drama_id: dramaId,
        episode_number: i + 1,
        watched_at: new Date().toISOString(),
        completed: true,
        progress_seconds: 0,
      }));

      const { error } = await supabase
        .from('watched_episodes')
        .upsert(watchedData, {
          onConflict: 'user_id,drama_id,episode_number',
        });

      if (error) {
        console.error('Error marking all episodes as watched:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: { dramaId, totalEpisodes },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error marking all episodes as watched:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to mark all episodes as watched',
        success: false,
      };
    }
  },

  clearAllWatched: async (dramaId: number): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      const { error } = await supabase
        .from('watched_episodes')
        .delete()
        .eq('user_id', user.id)
        .eq('drama_id', dramaId);

      if (error) {
        console.error('Error clearing watched episodes:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: { dramaId },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error clearing watched episodes:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to clear watched episodes',
        success: false,
      };
    }
  },
};

// Export group badges API
export const groupBadgesApi = {
  getMemberBadges: async (groupId: string, userId: string): Promise<ApiResponse<any>> => {
    try {
      // Fetch member's badges and level from group_members table
      const { data, error } = await supabase
        .from('group_members')
        .select('badges, level')
        .eq('group_id', groupId)
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching member badges:', error);
        return {
          data: null,
          error: error.message,
          success: false,
        };
      }

      return {
        data: {
          badges: data?.badges || [],
          level: data?.level || 1,
        },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error fetching member badges:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to fetch badges',
        success: false,
      };
    }
  },

  checkAndAwardBadges: async (groupId: string): Promise<ApiResponse<any>> => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        return {
          data: null,
          error: 'Authentication required',
          success: false,
        };
      }

      // Fetch current member data
      const { data: memberData, error: memberError } = await supabase
        .from('group_members')
        .select('badges, level, posts_count, comments_count, joined_at')
        .eq('group_id', groupId)
        .eq('user_id', user.id)
        .single();

      if (memberError) {
        console.error('Error fetching member data:', memberError);
        return {
          data: null,
          error: memberError.message,
          success: false,
        };
      }

      const currentBadges = memberData?.badges || [];
      const newBadges = [...currentBadges];
      let level = memberData?.level || 1;

      // Check for badge eligibility
      // Founding Member: Join within first 10 members
      if (!newBadges.includes('founding_member')) {
        const { count } = await supabase
          .from('group_members')
          .select('*', { count: 'exact', head: true })
          .eq('group_id', groupId);

        if (count && count <= 10) {
          newBadges.push('founding_member');
        }
      }

      // Top Contributor: 50+ posts
      if (!newBadges.includes('top_contributor') && (memberData?.posts_count || 0) >= 50) {
        newBadges.push('top_contributor');
      }

      // Active Participant: 100+ comments
      if (!newBadges.includes('active_participant') && (memberData?.comments_count || 0) >= 100) {
        newBadges.push('active_participant');
      }

      // Group Veteran: Member for 6+ months
      if (!newBadges.includes('group_veteran') && memberData?.joined_at) {
        const joinedDate = new Date(memberData.joined_at);
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        
        if (joinedDate <= sixMonthsAgo) {
          newBadges.push('group_veteran');
        }
      }

      // Content Creator: 20+ posts
      if (!newBadges.includes('content_creator') && (memberData?.posts_count || 0) >= 20) {
        newBadges.push('content_creator');
      }

      // Calculate level based on activity
      const totalActivity = (memberData?.posts_count || 0) + (memberData?.comments_count || 0);
      level = Math.floor(totalActivity / 10) + 1;

      // Update badges and level if changed
      if (newBadges.length !== currentBadges.length || level !== memberData?.level) {
        const { error: updateError } = await supabase
          .from('group_members')
          .update({
            badges: newBadges,
            level,
            updated_at: new Date().toISOString(),
          })
          .eq('group_id', groupId)
          .eq('user_id', user.id);

        if (updateError) {
          console.error('Error updating badges:', updateError);
          return {
            data: null,
            error: updateError.message,
            success: false,
          };
        }
      }

      return {
        data: {
          badges: newBadges,
          level,
        },
        error: null,
        success: true,
      };
    } catch (err) {
      console.error('Unexpected error checking badges:', err);
      return {
        data: null,
        error: err instanceof Error ? err.message : 'Failed to check badges',
        success: false,
      };
    }
  },
};
